import { CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { MatchQualityBadge } from '@/components/evaluation/match-quality-badge'
import { PersonListCard } from '@/components/person-list-card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ListCardSkeleton } from '@/components/layout/list-card-skeleton'
import { dataBenchDisplayName } from '@/components/platform/platform-data-bench-person-canvas'
import type { SimulationJob, SimulationProfile } from '@/hooks/use-platform-simulation'

function enrichChip(profile: SimulationProfile) {
  const status = profile.enrich_status
  if (!status) return null
  if (status === 'pending' || status === 'enriching') {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground" role="status">
        <Loader2 className="size-3 animate-spin" aria-hidden />
        Filling profile from CV…
      </span>
    )
  }
  if (status === 'failed') {
    const detail = profile.enrich_summary?.trim()
    return (
      <span
        className="flex items-center gap-1 text-xs text-destructive"
        role="alert"
        title={detail || undefined}
      >
        <XCircle className="size-3 shrink-0" aria-hidden />
        Could not fill from CV
      </span>
    )
  }
  if (status === 'ready') {
    return (
      <Badge variant="successSoft">
        <CheckCircle2 className="mr-1 size-3" aria-hidden />
        Profile filled from CV
      </Badge>
    )
  }
  return null
}

function subtitleFor(profile: SimulationProfile): string {
  const skills = profile.skill_count ?? 0
  const roles = profile.experience_count ?? 0
  if (skills || roles) {
    const role =
      profile.recent_role_title && profile.recent_role_company
        ? `${profile.recent_role_title} · ${profile.recent_role_company}`
        : profile.recent_role_title || profile.headline || profile.email
    return `${role} · ${skills} skill${skills === 1 ? '' : 's'} · ${roles} role${roles === 1 ? '' : 's'}`
  }
  return profile.headline || profile.email || ''
}

export function DataBenchPeopleList({
  profiles,
  job,
  generatingPlaceholders = 0,
  scoringIds = [],
  onOpen,
  selectedIds,
  onToggleSelected,
}: {
  profiles: SimulationProfile[]
  job?: SimulationJob | null
  generatingPlaceholders?: number
  scoringIds?: string[]
  onOpen: (profile: SimulationProfile) => void
  selectedIds?: string[]
  onToggleSelected?: (profileId: string) => void
}) {
  const scoring = job?.kind === 'pass1' && (job.status === 'queued' || job.status === 'running')

  return (
    <ul className="space-y-2">
      {profiles.map((profile) => {
        const isScoring = scoring && scoringIds.includes(profile.id) && !profile.evaluation_id
        const isQueued = scoring && !profile.evaluation_id && !isScoring
        const name = dataBenchDisplayName(profile)
        return (
          <li key={profile.id} className="flex items-start gap-2">
            {onToggleSelected ? (
              <Checkbox
                className="mt-3"
                checked={selectedIds?.includes(profile.id) ?? false}
                onCheckedChange={() => onToggleSelected(profile.id)}
                aria-label={`Select ${name}`}
              />
            ) : null}
            <div className="min-w-0 flex-1">
            <PersonListCard
              variant="compact"
              person={{
                id: profile.id,
                name: dataBenchDisplayName(profile),
                subtitle: subtitleFor(profile),
                colorSeed: profile.id,
              }}
              onOpen={() => onOpen(profile)}
              openAriaLabel={`Open ${dataBenchDisplayName(profile)}`}
              headerAside={
                profile.final_score != null ? (
                  <MatchQualityBadge score={profile.final_score} />
                ) : isScoring ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground" role="status">
                    <Loader2 className="size-3 animate-spin" aria-hidden />
                    Scoring
                  </span>
                ) : isQueued ? (
                  <span className="text-xs text-muted-foreground">Queued</span>
                ) : (
                  enrichChip(profile)
                )
              }
            />
            </div>
          </li>
        )
      })}
      {Array.from({ length: generatingPlaceholders }).map((_, index) => (
        <li key={`generating-${index}`}>
          <ListCardSkeleton embedded showFooter={false} />
          <p className="px-1 pt-1 text-xs text-muted-foreground" role="status">
            Generating person…
          </p>
        </li>
      ))}
    </ul>
  )
}
